import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useWs } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState, fmtDateTime } from "@/components/shared";
import { SavedViews } from "@/components/SavedViews";
import { LeadImportDialog } from "@/components/leads/LeadImportDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Table2, Plus, Loader2, Search, Trash2, Settings2, Webhook, Copy, Check, GripVertical,
  X, RadioTower, Upload, Zap, ChevronDown, AlertTriangle, GitMerge, Download, StickyNote, Pencil,
} from "lucide-react";

const FIELD_TYPES = ["text", "email", "phone", "number", "date", "url", "select"];
const STATUS_TINTS = ["#723FFF", "#3B82F6", "#F59E0B", "#30BD79", "#F95B5F", "#9333ea", "#0ea5e9"];

export default function Leads() {
  const [lists, setLists] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [mergeLead, setMergeLead] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [panelLead, setPanelLead] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const activeList = useMemo(() => lists.find((l) => l.id === activeId), [lists, activeId]);

  const loadLists = async (keepId) => {
    const { data } = await api.get("/leads/lists");
    setLists(data.lists);
    setCanEdit(data.can_edit);
    const has = (x) => x && data.lists.some((l) => l.id === x);
    const id = has(keepId) ? keepId : (data.lists[0]?.id || null);
    setActiveId((cur) => (has(keepId) || !has(cur) ? id : cur));
    return id;
  };

  useEffect(() => {
    (async () => { setLoading(true); try { await loadLists(); } finally { setLoading(false); } })();
  }, []);

  const loadLeads = async (id) => {
    if (!id) { setLeads([]); setReport(null); return; }
    const [{ data: ld }, { data: rp }] = await Promise.all([
      api.get(`/leads/lists/${id}/leads`),
      api.get(`/leads/lists/${id}/report`),
    ]);
    setLeads(ld.leads);
    setReport(rp);
  };
  useEffect(() => { loadLeads(activeId); /* eslint-disable-next-line */ }, [activeId]);

  const createList = async () => {
    try {
      const { data } = await api.post("/leads/lists", { name: newListName });
      setNewListOpen(false); setNewListName("");
      await loadLists(data.id);
      toast.success("List created");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (leadId, status) => {
    try {
      const { data } = await api.put(`/leads/${leadId}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? data : l)));
      loadReport();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const loadReport = async () => { if (activeId) { const { data } = await api.get(`/leads/lists/${activeId}/report`); setReport(data); } };

  const deleteLead = async (id) => {
    await api.delete(`/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    loadReport();
  };

  const deleteList = async () => {
    await api.delete(`/leads/lists/${activeId}`);
    toast.success("List deleted");
    await loadLists();
  };

  const exportCsv = () => {
    if (!activeList) return;
    const cols = activeList.fields || [];
    const header = ["Status", "Duplicate", ...cols.map((c) => c.label), "Received"];
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const dupLabel = (l) => (l.is_duplicate ? (l.dup_status === "ignored" ? "Ignored" : "Review") : "");
    const rows = filtered.map((l) => [l.status, dupLabel(l), ...cols.map((c) => l.data?.[c.key] ?? ""), l.created_at]);
    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(activeList.name || "leads").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads`);
  };

  const statusColor = (list, status) => {
    const idx = (list?.statuses || []).indexOf(status);
    return STATUS_TINTS[idx % STATUS_TINTS.length] || "#9FA1A4";
  };

  const filtered = useMemo(() => {
    let r = leads;
    if (statusFilter !== "all") r = r.filter((l) => l.status === statusFilter);
    if (search) { const s = search.toLowerCase(); r = r.filter((l) => Object.values(l.data || {}).some((v) => String(v).toLowerCase().includes(s))); }
    return r;
  }, [leads, search, statusFilter]);

  if (loading) return <div className="text-[#9FA1A4] text-sm">Loading…</div>;

  return (
    <div>
      <PageHeader
        title="Leads"
        subtitle="Your website form submissions, captured safely inside the portal."
        action={
          <div className="flex items-center gap-2">
            {canEdit && <Button data-testid="leads-import-button" variant="outline" onClick={() => setImportOpen(true)} className="border-[#EEEFEF] bg-transparent text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C] rounded-full"><Upload className="h-4 w-4 mr-1.5" /> Import</Button>}
            {activeList && canEdit && (
              <>
                <Button data-testid="leads-webhook-button" variant="outline" onClick={() => setWebhookOpen(true)} className="border-[#EEEFEF] bg-transparent text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C] rounded-full"><Webhook className="h-4 w-4 mr-1.5" /> Webhook</Button>
                <Button data-testid="leads-edit-list-button" variant="outline" onClick={() => setEditOpen(true)} className="border-[#EEEFEF] bg-transparent text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C] rounded-full"><Settings2 className="h-4 w-4 mr-1.5" /> Edit list</Button>
                <Button data-testid="leads-add-lead-button" onClick={() => setAddOpen(true)} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> Add lead</Button>
              </>
            )}
          </div>
        }
      />

      {importOpen && <LeadImportDialog lists={lists} activeId={activeId} onClose={() => setImportOpen(false)} onDone={async (id) => { setImportOpen(false); await loadLists(id); loadLeads(id); }} />}
      {lists.length === 0 ? (
        canEdit ? (
          <div className="rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-8 max-w-xl">
            <div className="h-11 w-11 rounded-lg bg-[#EDE7FF] border border-[#C9B8FF] flex items-center justify-center mb-5"><Table2 className="h-5 w-5 text-[#723FFF]" /></div>
            <h2 className="font-display text-xl font-medium mb-2">Create your first lead list</h2>
            <p className="text-sm text-[#9FA1A4] mb-6">A list is like a spreadsheet tab - its own columns and statuses, fed by a website webhook.</p>
            <Button data-testid="leads-create-first" onClick={() => setNewListOpen(true)} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New list</Button>
          </div>
        ) : (
          <EmptyState icon={Table2} title="No lead lists yet" description="Your agency will set up lead capture here soon." testid="leads-empty" />
        )
      ) : (
        <div>
          {/* List tabs */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            {lists.map((l) => (
              <button key={l.id} data-testid="leads-list-tab" onClick={() => setActiveId(l.id)}
                className={`rounded-full px-4 py-1.5 text-sm transition-all ${l.id === activeId ? "bg-[#06060C] text-white" : "bg-[#F6F6FD] text-[#595D62] hover:bg-[#EDE7FF] hover:text-[#5b2fd6]"}`}>
                {l.name} <span className={`ml-1.5 font-mono-data text-[10px] ${l.id === activeId ? "text-white/60" : "text-[#c4c5cc]"}`}>{l.lead_count}</span>
              </button>
            ))}
            {canEdit && (
              <button data-testid="leads-new-list-tab" onClick={() => setNewListOpen(true)} className="rounded-full h-8 w-8 flex items-center justify-center bg-[#F6F6FD] text-[#595D62] hover:bg-[#EDE7FF] hover:text-[#5b2fd6]"><Plus className="h-4 w-4" /></button>
            )}
          </div>

          {/* Report strip */}
          {report && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <ReportCard label="Total leads" value={report.total} />
              <ReportCard label="Converted" value={report.converted} accent="#30BD79" />
              <ReportCard label="Conversion rate" value={`${report.conversion_rate}%`} accent="#723FFF" />
              <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4">
                <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">By status</p>
                <div className="flex flex-wrap gap-1.5">
                  {(activeList?.statuses || []).map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]" style={{ backgroundColor: `${statusColor(activeList, s)}1a`, color: statusColor(activeList, s) }}>
                      {s} {report.by_status?.[s] || 0}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Source breakdown */}
          {report && (report.source_field || canEdit) && (
            <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Leads by source</p>
                {canEdit && (
                  <Select value={activeList?.source_field || "none"} onValueChange={async (v) => { await api.put(`/leads/lists/${activeId}`, { source_field: v === "none" ? "" : v }); await loadLists(activeId); loadReport(); }}>
                    <SelectTrigger data-testid="leads-source-select" className="h-7 w-48 bg-[#F6F6FD] border-[#EEEFEF] text-xs"><SelectValue placeholder="Pick a source column" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF]">
                      <SelectItem value="none">No source column</SelectItem>
                      {(activeList?.fields || []).map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {report.source_field ? (
                Object.keys(report.by_source || {}).length ? (
                  <div className="space-y-1.5 mt-2">
                    {Object.entries(report.by_source).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
                      const pct = report.total ? Math.round((v / report.total) * 100) : 0;
                      return (
                        <div key={k} className="flex items-center gap-3">
                          <span className="text-xs text-[#595D62] w-40 truncate">{k}</span>
                          <div className="flex-1 h-2 rounded-full bg-[#F6F6FD] overflow-hidden"><div className="h-full bg-[#723FFF]" style={{ width: `${pct}%` }} /></div>
                          <span className="font-mono-data text-[10px] text-[#9FA1A4] w-12 text-right">{v} · {pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                ) : <p className="text-xs text-[#c4c5cc]">No data yet.</p>
              ) : <p className="text-xs text-[#c4c5cc]">Pick a column to break leads down by source.</p>}
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#c4c5cc]" />
              <Input data-testid="leads-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads…" className="pl-9 bg-[#F6F6FD] border-[#EEEFEF]" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="leads-status-filter" className="w-44 bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#EEEFEF]">
                <SelectItem value="all">All statuses</SelectItem>
                {(activeList?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <SavedViews screen="leads" state={{ statusFilter }} defaultState={{ statusFilter: "all" }} onApply={(s) => setStatusFilter(s?.statusFilter || "all")} />
            <div className="ml-auto flex items-center gap-3">
              <Button data-testid="leads-export-button" variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} className="border-[#EEEFEF] bg-white text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C] rounded-full"><Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV</Button>
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{filtered.length} leads</span>
            </div>
          </div>

          {/* Table */}
          {(activeList?.fields || []).length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] p-10 text-center">
              <p className="text-sm text-[#9FA1A4]">{canEdit ? "This list has no columns yet. Click \"Edit list\" to add fields, then connect a webhook." : "This list is being set up."}</p>
            </div>
          ) : (
            <div className="rounded-3xl border border-[#EEEFEF] bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-[#EEEFEF] bg-[#FCFCFE]">
                      <th className="text-left px-4 py-3 font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Status</th>
                      <th className="text-left px-4 py-3 font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">Duplicate</th>
                      {activeList.fields.map((f) => (
                        <th key={f.key} className="text-left px-4 py-3 font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] whitespace-nowrap">{f.label}</th>
                      ))}
                      <th className="text-left px-4 py-3 font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] whitespace-nowrap">Received</th>
                      <th className="text-left px-4 py-3 font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] whitespace-nowrap">Notes</th>
                      {canEdit && <th className="w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((lead) => (
                      <tr key={lead.id} data-testid="leads-table-row" onClick={() => setPanelLead(lead)} className={`border-b border-[#f0f0f3] transition-colors cursor-pointer ${lead.is_duplicate ? "bg-[#F95B5F]/[0.07] hover:bg-[#F95B5F]/[0.12]" : "hover:bg-[#F6F6FD]"}`}>
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <Select value={lead.status} onValueChange={(v) => setStatus(lead.id, v)}>
                            <SelectTrigger data-testid="lead-status-select" className="h-8 w-36 border-0 text-xs font-medium" style={{ backgroundColor: `${statusColor(activeList, lead.status)}1a`, color: statusColor(activeList, lead.status) }}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-[#EEEFEF]">
                              {(activeList.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          {lead.is_duplicate ? (
                            <button data-testid="lead-duplicate-badge" onClick={() => setMergeLead(lead)} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${lead.dup_status === "ignored" ? "bg-[#9FA1A4]/15 text-[#7a7d82] hover:bg-[#9FA1A4]/25" : "bg-[#F95B5F]/15 text-[#d83a3f] hover:bg-[#F95B5F]/25"}`}>
                              <AlertTriangle className="h-3 w-3" /> {lead.dup_status === "ignored" ? "Ignored" : "Review"}
                            </button>
                          ) : <span className="text-[#c4c5cc] text-xs">-</span>}
                        </td>
                        {activeList.fields.map((f) => (
                          <td key={f.key} className="px-4 py-2.5 text-[#595D62] whitespace-nowrap max-w-[240px] truncate">{formatCell(f, lead.data?.[f.key])}</td>
                        ))}
                        <td className="px-4 py-2.5 font-mono-data text-[10px] text-[#c4c5cc] whitespace-nowrap">{fmtDateTime(lead.created_at)}</td>
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <button data-testid="lead-notes-button" onClick={() => setNotesLead(lead)} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${lead.note_count ? "bg-[#EDE7FF] text-[#5b2fd6] hover:bg-[#e0d5ff]" : "bg-[#F6F6FD] text-[#9FA1A4] hover:bg-[#EDE7FF] hover:text-[#5b2fd6]"}`}>
                            <StickyNote className="h-3 w-3" /> {lead.note_count ? lead.note_count : "Add"}
                          </button>
                        </td>
                        {canEdit && (
                          <td className="px-2" onClick={(e) => e.stopPropagation()}>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><button data-testid="lead-delete" className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                              <AlertDialogContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                                <AlertDialogHeader><AlertDialogTitle>Delete this lead?</AlertDialogTitle><AlertDialogDescription className="text-[#9FA1A4]">This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-[#EEEFEF]">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => deleteLead(lead.id)} className="bg-rose-500 hover:bg-rose-600 text-white">Delete</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        )}
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={activeList.fields.length + (canEdit ? 5 : 4)} className="px-4 py-10 text-center text-sm text-[#c4c5cc]">No leads yet. They'll appear here as your website form submits.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* New list dialog */}
      <Dialog open={newListOpen} onOpenChange={setNewListOpen}>
        <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-sm">
          <DialogHeader><DialogTitle className="font-display">New lead list</DialogTitle></DialogHeader>
          <Input data-testid="new-list-name" value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder="e.g. Contact form, Newsletter" className="bg-[#F6F6FD] border-[#EEEFEF]" onKeyDown={(e) => e.key === "Enter" && newListName.trim() && createList()} />
          <DialogFooter><Button data-testid="new-list-submit" onClick={createList} disabled={!newListName.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Create list</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {activeList && editOpen && <ListEditorDialog list={activeList} onClose={() => setEditOpen(false)} onSaved={(id) => loadLists(id)} onDeleted={deleteList} />}
      {activeList && addOpen && <AddLeadDialog list={activeList} onClose={() => setAddOpen(false)} onSaved={() => loadLeads(activeId)} />}
      {activeList && webhookOpen && <WebhookDialog list={activeList} onClose={() => setWebhookOpen(false)} onFieldsChanged={(id) => loadLists(id)} />}
      {activeList && mergeLead && (
        <MergeDialog
          list={activeList}
          lead={mergeLead}
          duplicates={(mergeLead.duplicate_ids || []).map((id) => leads.find((l) => l.id === id)).filter(Boolean)}
          canEdit={canEdit}
          onClose={() => setMergeLead(null)}
          onDone={() => { setMergeLead(null); loadLeads(activeId); }}
        />
      )}
      {activeList && notesLead && (
        <NotesSheet
          list={activeList}
          lead={notesLead}
          onClose={() => setNotesLead(null)}
          onCountChange={(count) => setLeads((ls) => ls.map((l) => (l.id === notesLead.id ? { ...l, note_count: count } : l)))}
        />
      )}
      {activeList && panelLead && (
        <Sheet open onOpenChange={(o) => { if (!o) setPanelLead(null); }}>
          <SheetContent side="right" data-testid="lead-detail-sheet" className="bg-white border-l border-[#EEEFEF] text-[#06060C] w-full sm:max-w-lg overflow-y-auto p-0">
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#EEEFEF] text-left">
              <SheetTitle className="font-display text-xl text-[#06060C]">Lead details</SheetTitle>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ backgroundColor: `${statusColor(activeList, panelLead.status)}1a`, color: statusColor(activeList, panelLead.status) }}>{panelLead.status}</span>
                {panelLead.is_duplicate && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium bg-[#F95B5F]/15 text-[#d83a3f]"><AlertTriangle className="h-3 w-3" /> Possible duplicate</span>}
                <span className="font-mono-data text-[10px] text-[#9FA1A4]">Received {fmtDateTime(panelLead.created_at)}</span>
              </div>
            </SheetHeader>
            <div className="px-6 py-5 grid gap-3 sm:grid-cols-2" data-testid="lead-detail-fields">
              {(activeList.fields || []).map((f) => (
                <div key={f.key} className={`rounded-xl border border-[#f0f0f3] bg-[#FCFCFE] px-3.5 py-2.5 ${f.type === "textarea" ? "sm:col-span-2" : ""}`}>
                  <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-1">{f.label}</p>
                  <p data-testid={`lead-field-${f.key}`} className="text-sm text-[#06060C] break-words whitespace-pre-wrap">{formatCell(f, panelLead.data?.[f.key]) || <span className="text-[#c4c5cc]">—</span>}</p>
                </div>
              ))}
            </div>
            <div className="px-6 pb-6 flex items-center gap-2">
              <button data-testid="lead-detail-notes" onClick={() => { const l = panelLead; setPanelLead(null); setNotesLead(l); }} className="inline-flex items-center gap-1.5 rounded-full bg-[#EDE7FF] text-[#5b2fd6] px-3.5 py-1.5 text-xs font-medium hover:bg-[#e0d5ff]"><StickyNote className="h-3.5 w-3.5" /> Notes {panelLead.note_count ? `(${panelLead.note_count})` : ""}</button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}

function ReportCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4">
      <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4]">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold" style={{ color: accent || "#06060C" }}>{value}</p>
    </div>
  );
}

function formatCell(field, value) {
  if (value == null || value === "") return "-";
  if (field.type === "url") return <a href={value} target="_blank" rel="noreferrer" className="text-[#723FFF] hover:underline">{value}</a>;
  if (field.type === "email") return <a href={`mailto:${value}`} className="text-[#723FFF] hover:underline">{value}</a>;
  return String(value);
}

/* ---------- List editor ---------- */
function ListEditorDialog({ list, onClose, onSaved, onDeleted }) {
  const [name, setName] = useState(list.name);
  const [fields, setFields] = useState(list.fields || []);
  const [statuses, setStatuses] = useState(list.statuses || []);
  const [saving, setSaving] = useState(false);

  const addField = () => setFields((f) => [...f, { label: "", type: "text", options: [] }]);
  const updateField = (i, patch) => setFields((f) => f.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const removeField = (i) => setFields((f) => f.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        fields: fields.filter((f) => f.label.trim()).map((f) => ({ ...f, options: f.type === "select" ? (f.options || []).filter(Boolean) : null })),
        statuses: statuses.filter((s) => s.trim()),
      };
      const { data } = await api.put(`/leads/lists/${list.id}`, payload);
      toast.success("List updated");
      onClose();
      onSaved(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Edit list structure</DialogTitle></DialogHeader>
        <div className="space-y-6 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">List name</label>
            <Input data-testid="edit-list-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-[#9FA1A4] font-mono-data uppercase tracking-wider">Fields (columns)</label>
              <button data-testid="add-field-button" onClick={addField} className="text-xs text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add field</button>
            </div>
            <div className="space-y-2">
              {fields.map((f, i) => (
                <div key={i} data-testid="field-row" className="rounded-xl border border-[#f0f0f3] bg-[#FCFCFE] p-2.5">
                  <div className="flex items-center gap-2">
                    <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Column label" className="bg-white border-[#EEEFEF] h-8 text-sm flex-1" data-testid="field-label-input" />
                    <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                      <SelectTrigger className="w-32 bg-white border-[#EEEFEF] h-8 text-xs" data-testid="field-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#EEEFEF]">{FIELD_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => removeField(i)} className="text-[#c4c5cc] hover:text-rose-600 p-1"><X className="h-4 w-4" /></button>
                  </div>
                  {f.type === "select" && (
                    <Input value={(f.options || []).join(", ")} onChange={(e) => updateField(i, { options: e.target.value.split(",").map((o) => o.trim()) })} placeholder="Options, comma separated" className="bg-white border-[#EEEFEF] h-8 text-sm mt-2" />
                  )}
                </div>
              ))}
              {fields.length === 0 && <p className="text-xs text-[#c4c5cc]">No fields yet.</p>}
            </div>
          </div>

          <div>
            <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Statuses (pipeline stages)</label>
            <Input data-testid="edit-statuses" value={statuses.join(", ")} onChange={(e) => setStatuses(e.target.value.split(",").map((s) => s.trimStart()))} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="New, Contacted, Qualified, Converted, Lost" />
            <p className="mt-1 text-[10px] text-[#c4c5cc]">"Converted" is used for the conversion-rate metric.</p>
          </div>
        </div>
        <DialogFooter className="justify-between">
          <AlertDialog>
            <AlertDialogTrigger asChild><button data-testid="delete-list-button" className="text-xs text-rose-600 hover:text-rose-700 flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete list</button></AlertDialogTrigger>
            <AlertDialogContent className="bg-white border-[#EEEFEF] text-[#06060C]">
              <AlertDialogHeader><AlertDialogTitle>Delete "{list.name}"?</AlertDialogTitle><AlertDialogDescription className="text-[#9FA1A4]">All leads and the webhook for this list will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-[#EEEFEF]">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { onClose(); onDeleted(); }} className="bg-rose-500 hover:bg-rose-600 text-white">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button data-testid="edit-list-save" onClick={save} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Add lead ---------- */
function AddLeadDialog({ list, onClose, onSaved }) {
  const [data, setData] = useState({});
  const [status, setStatus] = useState((list.statuses || [])[0] || "New");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/leads/lists/${list.id}/leads`, { data, status }); toast.success("Lead added"); onClose(); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-md">
        <DialogHeader><DialogTitle className="font-display">Add a lead</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
          {(list.fields || []).map((f) => (
            <div key={f.key}>
              <label className="text-xs text-[#9FA1A4] mb-1.5 block">{f.label}</label>
              {f.type === "select" ? (
                <Select value={data[f.key] || ""} onValueChange={(v) => setData({ ...data, [f.key]: v })}>
                  <SelectTrigger className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-white border-[#EEEFEF]">{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={data[f.key] || ""} onChange={(e) => setData({ ...data, [f.key]: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" />
              )}
            </div>
          ))}
          <div>
            <label className="text-xs text-[#9FA1A4] mb-1.5 block">Status</label>
            <Select value={status} onValueChange={setStatus}><SelectTrigger className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF]">{(list.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
        </div>
        <DialogFooter><Button data-testid="add-lead-save" onClick={save} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add lead"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Webhook + field mapping ---------- */
function WebhookDialog({ list, onClose, onFieldsChanged }) {
  const [wh, setWh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listening, setListening] = useState(false);
  const [mapping, setMapping] = useState({});
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const pollRef = useRef(null);

  const hookUrl = wh ? `${API}/leads/hooks/${wh.token}` : "";

  const load = async () => {
    setLoading(true);
    try {
      let { data } = await api.get(`/leads/lists/${list.id}/webhook`);
      if (!data.token) { ({ data } = await api.post(`/leads/lists/${list.id}/webhook`)); }
      setWh(data);
      setMapping(data.mapping || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); return () => clearInterval(pollRef.current); /* eslint-disable-next-line */ }, []);

  const startListening = async () => {
    setListening(true);
    await api.post(`/leads/webhooks/${wh.id}/listen`);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const { data } = await api.get(`/leads/webhooks/${wh.id}`);
      if (data.sample_payload && Object.keys(data.sample_payload).length) {
        setWh(data);
        clearInterval(pollRef.current);
        setListening(false);
        toast.success("Test data captured!");
      }
    }, 2000);
  };

  const copy = () => { navigator.clipboard.writeText(hookUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const incomingKeys = wh?.sample_payload ? Object.keys(wh.sample_payload) : [];

  const createFieldAndMap = async (incKey) => {
    // add a field to the list derived from the incoming key, then map to it
    const label = incKey.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const newFields = [...(list.fields || []), { label, type: "text", options: [] }];
    const { data } = await api.put(`/leads/lists/${list.id}`, { fields: newFields });
    onFieldsChanged(data.id);
    const created = data.fields[data.fields.length - 1];
    setMapping((m) => ({ ...m, [incKey]: created.key }));
    toast.success(`Field "${label}" created`);
  };

  const saveMapping = async (activate) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/webhooks/${wh.id}`, { mapping, active: activate });
      setWh(data);
      toast.success(activate ? "Webhook live - leads will flow in" : "Mapping saved");
      if (activate) onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-2xl" data-testid="webhook-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Webhook className="h-4 w-4 text-[#723FFF]" /> Webhook for "{list.name}"</DialogTitle></DialogHeader>
        {loading || !wh ? (
          <div className="py-10 flex justify-center text-[#9FA1A4]"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-6 py-2 max-h-[68vh] overflow-y-auto pr-1">
            {/* Step 1: URL */}
            <div>
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">1 · Point your website form here</p>
              <div className="flex items-center gap-2 rounded-xl border border-[#EEEFEF] bg-[#F6F6FD] p-2">
                <code className="flex-1 text-xs text-[#595D62] truncate px-1" data-testid="webhook-url">{hookUrl}</code>
                <Button size="sm" variant="outline" onClick={copy} className="border-[#EEEFEF] bg-white h-8 shrink-0">{copied ? <Check className="h-3.5 w-3.5 text-[#30BD79]" /> : <Copy className="h-3.5 w-3.5" />}</Button>
              </div>
              <p className="mt-1.5 text-[10px] text-[#c4c5cc]">Send a POST with JSON or form fields. {wh.active ? <span className="text-[#30BD79]">● Live</span> : <span className="text-[#F59E0B]">● Not live yet</span>} · {wh.received_count || 0} received</p>
            </div>

            {/* Step 2: Listen */}
            <div>
              <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">2 · Capture a test submission</p>
              <div className="flex items-center gap-2">
                <Button data-testid="webhook-listen-button" onClick={startListening} disabled={listening} variant="outline" className="border-[#EEEFEF] bg-white rounded-full">
                  {listening ? <><RadioTower className="h-4 w-4 mr-1.5 text-[#723FFF] animate-pulse" /> Listening…</> : <><RadioTower className="h-4 w-4 mr-1.5" /> Listen for test data</>}
                </Button>
                {wh.last_received_at && <span className="font-mono-data text-[10px] text-[#c4c5cc]">Last: {fmtDateTime(wh.last_received_at)}</span>}
              </div>
              {listening && <p className="mt-2 text-xs text-[#9FA1A4]">Now submit your website form (or POST test data) to the URL above…</p>}
            </div>

            {/* Step 3: Map */}
            {incomingKeys.length > 0 && (
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">3 · Map incoming fields → your columns</p>
                <div className="space-y-2">
                  {incomingKeys.map((k) => (
                    <div key={k} data-testid="mapping-row" className="flex items-center gap-2 rounded-xl border border-[#f0f0f3] bg-[#FCFCFE] p-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#06060C] font-medium truncate">{k}</p>
                        <p className="text-[10px] text-[#c4c5cc] truncate">e.g. {String(wh.sample_payload[k]).slice(0, 40) || "-"}</p>
                      </div>
                      <ChevronDown className="h-3.5 w-3.5 text-[#c4c5cc] -rotate-90" />
                      <div className="w-48 shrink-0">
                        <Select value={mapping[k] || "ignore"} onValueChange={(v) => { if (v === "__create") return createFieldAndMap(k); setMapping((m) => ({ ...m, [k]: v === "ignore" ? "" : v })); }}>
                          <SelectTrigger data-testid="mapping-select" className="bg-white border-[#EEEFEF] h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-[#EEEFEF]">
                            <SelectItem value="ignore">Ignore</SelectItem>
                            {(list.fields || []).map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                            <SelectItem value="__create" className="text-[#723FFF]">+ Create column "{k}"</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {wh && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => saveMapping(false)} disabled={saving || !incomingKeys.length} className="border-[#EEEFEF] bg-white rounded-full">Save mapping</Button>
            <Button data-testid="webhook-activate" onClick={() => saveMapping(true)} disabled={saving || !incomingKeys.length} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Zap className="h-4 w-4 mr-1.5" /> {wh.active ? "Update & keep live" : "Save & go live"}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ---------- Duplicate merge / ignore ---------- */
function MergeDialog({ list, lead, duplicates, canEdit, onClose, onDone }) {
  const records = [lead, ...duplicates];
  const [chosen, setChosen] = useState(() => {
    const init = {};
    for (const f of list.fields || []) {
      let val = lead.data?.[f.key];
      if (val == null || val === "") {
        const alt = duplicates.find((d) => d.data?.[f.key]);
        if (alt) val = alt.data[f.key];
      }
      init[f.key] = val ?? "";
    }
    return init;
  });
  const [busy, setBusy] = useState(false);

  const doMerge = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/merge`, { data: chosen, remove_ids: duplicates.map((d) => d.id) });
      toast.success("Leads merged");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const doIgnore = async () => {
    setBusy(true);
    try {
      await api.put(`/leads/${lead.id}/dup-status`);
      toast.success("Marked as ignored");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-2xl" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-[#F95B5F]" /> Possible duplicate</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#595D62] -mt-1">
          We found <strong>{duplicates.length}</strong> other lead{duplicates.length > 1 ? "s" : ""} sharing this email or phone.
          {canEdit ? " Pick the value to keep for each field, then merge into one clean record." : " Review the matches below."}
        </p>
        {lead.dup_status === "ignored" && (
          <p className="text-xs text-[#9FA1A4]">Currently ignored{lead.dup_ignored_by ? ` by ${lead.dup_ignored_by}` : ""}.</p>
        )}

        <div className="space-y-3 py-2 max-h-[55vh] overflow-y-auto pr-1">
          {(list.fields || []).map((f) => {
            const values = [...new Set(records.map((r) => r.data?.[f.key]).filter((v) => v != null && v !== ""))];
            return (
              <div key={f.key} className="rounded-xl border border-[#f0f0f3] bg-[#FCFCFE] p-3" data-testid="merge-field-row">
                <p className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-2">{f.label}</p>
                {values.length === 0 ? (
                  <p className="text-xs text-[#c4c5cc]">-</p>
                ) : canEdit ? (
                  <div className="flex flex-wrap gap-2">
                    {values.map((v) => (
                      <button key={String(v)} onClick={() => setChosen((c) => ({ ...c, [f.key]: v }))}
                        className={`rounded-lg border px-3 py-1.5 text-sm transition-all ${String(chosen[f.key]) === String(v) ? "border-[#723FFF] bg-[#EDE7FF] text-[#5b2fd6]" : "border-[#EEEFEF] bg-white text-[#595D62] hover:border-[#d3d4da]"}`}>
                        {String(v)}{String(chosen[f.key]) === String(v) && <Check className="inline h-3 w-3 ml-1.5" />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#595D62]">{values.map(String).join("  ·  ")}</p>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter className="gap-2">
          <Button data-testid="merge-ignore-button" variant="outline" onClick={doIgnore} disabled={busy} className="border-[#EEEFEF] bg-white rounded-full">Ignore</Button>
          {canEdit && (
            <Button data-testid="merge-confirm-button" onClick={doMerge} disabled={busy} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GitMerge className="h-4 w-4 mr-1.5" /> Merge {records.length} into one</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Lead notes ---------- */
function NotesSheet({ list, lead, onClose, onCountChange }) {
  const [notes, setNotes] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");

  const firstField = (list.fields || [])[0];
  const title = (firstField && lead.data?.[firstField.key]) || "Lead";

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${lead.id}/notes`);
      setNotes(data.notes);
      setCurrentUserId(data.current_user_id);
      onCountChange(data.notes.length);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/notes`, { body: text });
      setBody("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    const text = editBody.trim();
    if (!text) return;
    setBusy(true);
    try {
      await api.put(`/leads/${lead.id}/notes/${id}`, { body: text });
      setEditingId(null); setEditBody("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!(await confirmDelete({ message: "Delete this note?" }))) return;
    try {
      await api.delete(`/leads/${lead.id}/notes/${id}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent data-testid="lead-notes-sheet" className="bg-white border-l border-[#EEEFEF] text-[#06060C] w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#EEEFEF]">
          <SheetTitle className="font-display flex items-center gap-2 text-[#06060C]"><StickyNote className="h-4 w-4 text-[#723FFF]" /> Notes</SheetTitle>
          <p className="text-sm text-[#9FA1A4] truncate">{String(title)}</p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {notes === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
          ) : notes.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#c4c5cc]">No notes yet. Add the first next step below.</div>
          ) : (
            notes.map((n) => (
              <div key={n.id} data-testid="lead-note-item" className="rounded-2xl border border-[#EEEFEF] bg-[#FCFCFE] p-3.5">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="text-xs font-medium text-[#06060C]">{n.author_name}</span>
                  <span className="font-mono-data text-[10px] text-[#c4c5cc] whitespace-nowrap">{fmtDateTime(n.created_at)}{n.updated_at !== n.created_at ? " · edited" : ""}</span>
                </div>
                {editingId === n.id ? (
                  <div className="space-y-2">
                    <Textarea data-testid="lead-note-edit-input" value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={3} className="text-sm border-[#EEEFEF] resize-none" />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setEditBody(""); }} className="border-[#EEEFEF] bg-white rounded-full h-7 text-xs">Cancel</Button>
                      <Button data-testid="lead-note-save-edit" size="sm" onClick={() => saveEdit(n.id)} disabled={busy || !editBody.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full h-7 text-xs">Save</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-[#595D62] whitespace-pre-wrap break-words">{n.body}</p>
                    {n.author_id === currentUserId && (
                      <div className="flex gap-3 mt-2">
                        <button data-testid="lead-note-edit" onClick={() => { setEditingId(n.id); setEditBody(n.body); }} className="text-[11px] text-[#9FA1A4] hover:text-[#723FFF] flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                        <button data-testid="lead-note-delete" onClick={() => remove(n.id)} className="text-[11px] text-[#9FA1A4] hover:text-rose-600 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-[#EEEFEF] px-6 py-4 space-y-2">
          <Textarea
            data-testid="lead-note-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) add(); }}
            placeholder="Add a note or next step…"
            rows={3}
            className="text-sm border-[#EEEFEF] resize-none"
          />
          <div className="flex justify-end">
            <Button data-testid="lead-note-submit" onClick={add} disabled={busy || !body.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add note</>}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
